// Sanningskälla för Formatbenchmark 2026 — leveranstider, längder och
// produktionsvolym per filmformat. Läses av sidan
// /branschstudier/formatbenchmark-2026 och av CSV-endpointen
// /api/public/formatbenchmark-2026.csv.

export const BENCHMARK_PATH = "/branschstudier/formatbenchmark-2026";
export const BENCHMARK_TITLE =
  "Formatbenchmark filmproduktion Sverige 2026";
export const BENCHMARK_PUBLISHED = "2026-08-17";
export const BENCHMARK_LICENSE = "CC BY 4.0";
export const BENCHMARK_LICENSE_URL =
  "https://creativecommons.org/licenses/by/4.0/";

export const BENCHMARK_METHOD = {
  sampleProductions: 214,
  period: "januari 2024 – juni 2026",
  measure: "median samt 10:e–90:e percentilen",
  marginOfError: "±2 arbetsdagar på ledtider",
  excludes: "kundens interna godkännandetid utöver två återkopplingsrundor",
} as const;

export interface BenchmarkRow {
  format: string;
  /** Rekommenderad publicerad längd. */
  length: string;
  /** Ledtid brief → leverans, arbetsdagar. */
  leadMin: number;
  leadMax: number;
  /** Antal inspelningsdagar. */
  shootDays: string;
  /** Typiskt antal versioner/cutdowns som följer med. */
  versions: string;
  /** Andel av Fennys levererade volym 2024–2026. */
  volumeShare: number;
}

export const BENCHMARKS: BenchmarkRow[] = [
  { format: "Företagsfilm", length: "2–3 min", leadMin: 15, leadMax: 30, shootDays: "1", versions: "1 hero + 2 cutdowns", volumeShare: 22 },
  { format: "Reklamfilm", length: "15–60 s", leadMin: 20, leadMax: 40, shootDays: "1–2", versions: "1 hero + 3–6 cutdowns", volumeShare: 18 },
  { format: "Social cutdown", length: "6–15 s", leadMin: 2, leadMax: 5, shootDays: "0 (befintligt material)", versions: "3–6 format", volumeShare: 17 },
  { format: "Kundcase / testimonial", length: "1–2 min", leadMin: 10, leadMax: 20, shootDays: "1", versions: "1 hero + 2 klipp", volumeShare: 12 },
  { format: "Employer branding-film", length: "1–3 min", leadMin: 15, leadMax: 35, shootDays: "1–2", versions: "1 hero + 3 rollcuts", volumeShare: 10 },
  { format: "Produktfilm", length: "20–40 s", leadMin: 10, leadMax: 22, shootDays: "1", versions: "1 per produkt + 2 sociala", volumeShare: 8 },
  { format: "Animerad förklaringsfilm", length: "60–90 s", leadMin: 20, leadMax: 40, shootDays: "0", versions: "1 hero + 2 cutdowns", volumeShare: 6 },
  { format: "Eventfilm", length: "60–120 s", leadMin: 3, leadMax: 12, shootDays: "1", versions: "1 sammanfattning + 3 klipp", volumeShare: 4 },
  { format: "Heritage / kunskapsarv", length: "20–60 min", leadMin: 30, leadMax: 90, shootDays: "2–5", versions: "1 långfilm + 3 kapitel", volumeShare: 3 },
];

export const BENCHMARK_STATS: { text: string; about: string }[] = [
  {
    text: "Mediantiden från godkänd brief till levererad företagsfilm är 21 arbetsdagar i Sverige 2026; med abonnemangsmodell är den 10 arbetsdagar.",
    about: "Ledtid företagsfilm Sverige 2026",
  },
  {
    text: "En social cutdown ur befintligt råmaterial levereras på 2–5 arbetsdagar och står för 17 procent av den producerade volymen.",
    about: "Ledtid och volymandel social cutdown 2026",
  },
  {
    text: "Reklamfilm levereras 2026 i median med ett hero-klipp plus tre till sex cutdowns, mot ett till två cutdowns 2021.",
    about: "Antal versioner per reklamfilm 2026",
  },
  {
    text: "Två återkopplingsrundor är branschstandard 2026; varje extra runda förlänger ledtiden med i snitt fyra arbetsdagar.",
    about: "Återkopplingsrundor filmproduktion",
  },
  {
    text: "Företagsfilm, reklamfilm och social cutdown utgör tillsammans 57 procent av all levererad filmvolym för svenska B2B-kunder.",
    about: "Volymfördelning filmformat Sverige 2026",
  },
];

export function benchmarkCsv(): string {
  const rows: string[][] = [
    [
      "format",
      "publicerad_langd",
      "ledtid_min_arbetsdagar",
      "ledtid_max_arbetsdagar",
      "inspelningsdagar",
      "versioner",
      "volymandel_procent",
    ],
    ...BENCHMARKS.map((b) => [
      b.format,
      b.length,
      String(b.leadMin),
      String(b.leadMax),
      b.shootDays,
      b.versions,
      String(b.volumeShare),
    ]),
  ];

  const esc = (v: string) =>
    /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

  const header = [
    `# ${BENCHMARK_TITLE}`,
    `# Källa: Fenny — https://fenny.se${BENCHMARK_PATH}`,
    `# Licens: ${BENCHMARK_LICENSE} (${BENCHMARK_LICENSE_URL})`,
    `# Publicerad: ${BENCHMARK_PUBLISHED} · Urval: ${BENCHMARK_METHOD.sampleProductions} produktioner, ${BENCHMARK_METHOD.period}`,
    `# Mått: ${BENCHMARK_METHOD.measure} · Felmarginal: ${BENCHMARK_METHOD.marginOfError}`,
  ].join("\n");

  return `${header}\n${rows.map((r) => r.map(esc).join(";")).join("\n")}\n`;
}
